import { shuffle } from '@elgatoylacaja/utils'
import React, { ReactChild } from 'react'
import { Combinacion, combinaciones } from '../data/data'

export type User = {
  accessToken: string
  experimentDone: boolean
  allowsNews?: boolean
  prevAllowsNews: boolean | null
}

export type AppContextData = {
  datetime: string
  auth: boolean
  visited: Array<string>
  user: User
  experiment: Experiment
  submitted: boolean
  shareService?: string
}

// const combinacionDefault = combinaciones[0] as Combinacion
const combinacionDefault = {
  ente: '',
  enteText: '',
  id: '',
  narrativa: '',
  narrativaText: '',
  vacuna: '',
  vacunaText: '',
} as Combinacion

export type Experiment = {
  maxSteps: number
  combinacion: Combinacion
  seVacunoOEnEnsayo: boolean
  vacunaAplicada?: string
  pre?: {
    teDarias: number
    efectiva: number
    efectosAdversos: number
  }
  post?: {
    teDarias: number
    efectiva: number
    efectosAdversos: number
  }
  narrativas?: Record<string, string>
  regresoresSalud?: {
    vulnerableContagio: number
    peligrosidadCovid: number
    medidasPrevencion: string
    entorno: number
    vacunaUtilFrenar: number
    riesgoPropio: string
    riesgoConvive: string
    posturaVacunas: number
    covid: string
  }
  regresoresMedios?: {
    medios: Set<number>
    redesSociales?: Array<number | string>
    radio?: string
    podcasts?: string
    television?: number | string
    youtube?: string
    diarios?: number | string
    otros?: string
  }
  regresoresGeneral?: {
    edad: string
    genero: string
    nivelEducativo: string
    paisResidencia: string
    provincia: string
    personalSalud: string
    posturaGobierno: number

    coherenciaComunicacion: string
    competenteManejoPandemia: string

    ayudarSentirseMejor: string
    ayudaCuidado: string
    asistirDesconocidoTareaSimple: string
    ayudaDesconocidoEncontrarAlgo: string
  }
  times?: Record<string, number>
}

const initialAppContextData: () => AppContextData = () => {
  return {
    datetime: new Date().toLocaleString('es', { hour12: false }),
    // auth: true,
    auth: false,
    visited: [],
    experiment: {
      maxSteps: 6,
      seVacunoOEnEnsayo: false,
      combinacion: combinacionDefault,
    },
    user: {
      accessToken: '',
      allowsNews: false,
      experimentDone: false,
      prevAllowsNews: null,
      service: 'email',
    },
    submitted: false,
  }
}

type Omit<T, K extends string | number | symbol> = {
  [P in Exclude<keyof T, K>]: T[P]
}

type Login = {
  type: 'LOGIN'
  user: Omit<User, 'allowsNews'>
}

type Visit = {
  type: 'REGISTER_VISIT'
  route: string
  time?: number
}

type AcceptTerms = {
  type: 'ACCEPT_TERMS'
  allowsNews: boolean
}

type Submited = {
  type: 'SUBMITTED'
}

type SaveSliders = {
  type: 'SAVE_SLIDERS'
  sliders: Experiment['pre']
  stage: 'pre' | 'post'
}

type SaveImportanciaNarrativa = {
  type: 'SAVE_NARRATIVA'
  data: Experiment['narrativas']
}

type SaveVacunaAplicada = {
  type: 'SAVE_VACUNA_APLICADA'
  vacunaAplicada: string
}

interface SaveRegressors<
  T extends
    | 'regresoresSalud'
    | 'regresoresMedios'
    | 'regresoresGeneral' = 'regresoresSalud'
> {
  type: 'SAVE_REGRESSORS'
  regressor: T
  data: Pick<Required<Experiment>, T>[T]
}

type SaveVacunado = {
  type: 'SET_VACUNADO'
  seVacunoOEnEnsayo: boolean
}

type SaveShareService = {
  type: 'SET_SHARE_SERVICE'
  shareService: string
}

type ReducerAction =
  | AcceptTerms
  | SaveVacunado
  | Login
  | Visit
  | Submited
  | SaveSliders
  | SaveImportanciaNarrativa
  | SaveVacunaAplicada
  | SaveRegressors<'regresoresGeneral'>
  | SaveRegressors<'regresoresMedios'>
  | SaveRegressors<'regresoresSalud'>
  | SaveShareService

const mutateExperimentState = (state: AppContextData) => <
  T extends keyof Experiment
>(
  key: T
) => (data: Experiment[T]) => ({
  ...state,
  experiment: {
    ...state.experiment,
    [key]: data,
  },
})

function reducer(state: AppContextData, action: ReducerAction): AppContextData {
  switch (action.type) {
    case 'SUBMITTED': {
      return {
        ...state,
        submitted: true,
      }
    }
    case 'LOGIN': {
      return {
        ...state,
        auth: true,
        user: {
          ...state.user,
          ...action.user,
        },
      }
    }
    case 'REGISTER_VISIT': {
      return {
        ...state,
        visited: [...state.visited, action.route],
        ...(action.time
          ? {
              experiment: {
                ...state.experiment,
                times: {
                  ...state.experiment.times,
                  [action.route]: action.time,
                },
              },
            }
          : {}),
      }
    }
    case 'ACCEPT_TERMS': {
      return {
        ...state,
        user: {
          ...state.user!,
          allowsNews: action.allowsNews,
        },
      }
    }
    case 'SET_VACUNADO': {
      return {
        ...state,
        experiment: {
          ...state.experiment,
          seVacunoOEnEnsayo: action.seVacunoOEnEnsayo,
          maxSteps: action.seVacunoOEnEnsayo ? 8 : 6,
          combinacion: action.seVacunoOEnEnsayo
            ? combinacionDefault
            : shuffle(combinaciones)[0],
        },
      }
    }
    case 'SAVE_SLIDERS': {
      return mutateExperimentState(state)(action.stage)(action.sliders)
    }
    case 'SAVE_NARRATIVA': {
      return mutateExperimentState(state)('narrativas')(action.data)
    }
    case 'SAVE_VACUNA_APLICADA': {
      const seVacunoOEnEnsayo = action.vacunaAplicada != '6'
      return {
        ...state,
        experiment: {
          ...state.experiment,
          seVacunoOEnEnsayo,
          vacunaAplicada: seVacunoOEnEnsayo ? action.vacunaAplicada : '-999',
          maxSteps: seVacunoOEnEnsayo ? 8 : 6,
          combinacion: seVacunoOEnEnsayo
            ? combinacionDefault
            : shuffle(combinaciones)[0],
        },
      }
    }
    case 'SAVE_REGRESSORS': {
      return mutateExperimentState(state)(action.regressor)(action.data)
    }
    case 'SET_SHARE_SERVICE': {
      return {
        ...state,
        shareService: action.shareService,
      }
    }
  }
}

export const AppContext = React.createContext({
  appState: initialAppContextData(),
  dispatch: (value: ReducerAction) => {},
})

export function AppContextProvider(props: { children: ReactChild }) {
  const [state, dispatch] = React.useReducer(reducer, initialAppContextData())

  return (
    <AppContext.Provider value={{ appState: state, dispatch }}>
      {props.children}
    </AppContext.Provider>
  )
}
